import { useRef, type PointerEvent } from "react";
import { cx } from "../ui";

// D66 — leichter Splitter (Eigenbau, KEINE Lib): Pointer-Capture statt window-Listener
// (kein Leak, robust bei schnellem Ziehen), live-Resize. axis "x" = vertikaler Griff
// (steuert eine Spaltenbreite), "y" = horizontaler Griff (steuert eine Zeilenhöhe).
// `sign=-1` kehrt die Richtung um, wenn der Griff am LINKEN/OBEREN Rand der gesteuerten
// Region sitzt (z.B. linker Rand der rechten Spalte → nach links ziehen vergrößert sie).

interface ResizeHandleProps {
  axis: "x" | "y";
  /** Lazy gelesen beim Drag-Start (kein stale closure). */
  getValue: () => number;
  /** Live-Resize während des Drags (nur Store, kein Persist). */
  onResize: (next: number) => void;
  /** Drag-Ende: einmaliges Persistieren (kein localStorage-Write je pointermove). */
  onCommit?: () => void;
  min?: number;
  max?: number;
  sign?: 1 | -1;
  label: string;
}

export function ResizeHandle({ axis, getValue, onResize, onCommit, min = 80, max = 1200, sign = 1, label }: ResizeHandleProps) {
  const drag = useRef<{ start: number; base: number } | null>(null);

  function down(e: PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { start: axis === "x" ? e.clientX : e.clientY, base: getValue() };
  }
  function move(e: PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d) return;
    const pos = axis === "x" ? e.clientX : e.clientY;
    onResize(Math.min(max, Math.max(min, d.base + (pos - d.start) * sign)));
  }
  // Gemeinsamer Drag-Ende-Pfad (pointerup/cancel/lostpointercapture) — idempotent, gibt die
  // Capture frei und committet einmalig. Deckt auch Capture-Verlust/Unmount sauber ab.
  function end(e: PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    onCommit?.();
  }

  return (
    <div
      role="separator"
      aria-orientation={axis === "x" ? "vertical" : "horizontal"}
      aria-label={label}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onLostPointerCapture={end}
      className={cx(
        "shrink-0 touch-none bg-border/30 transition-colors hover:bg-accent/50 active:bg-accent/60",
        axis === "x" ? "w-1 cursor-col-resize" : "h-1 cursor-row-resize",
      )}
    />
  );
}
